"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Compass, FileCheck, House as Home, Landmark, Ruler, Trees, Hammer, ShieldCheck } from 'lucide-react';
export default function DeckGuide() {
  return <section id="decaturdeckremodelingguide" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 border-chart-1/30 text-chart-1 bg-chart-1/5 px-3 py-1 font-medium tracking-wide uppercase text-xs">
            Planning & Local Guidelines
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground leading-tight">
            Decatur Deck Remodeling & Structural Rebuilding Guide
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            A comprehensive reference for navigating architectural site conditions,
            municipal permitting differences, and historic preservation standards
            when planning a deck rebuild in Decatur and surrounding neighborhoods.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Main Editorial Content */}
          <main className="lg:col-span-8 space-y-12">
            {/* Section 1: Architectural Conditions */}
            <article className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-chart-1/10 text-chart-1">
                  <Home className="size-5" />
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl font-normal text-foreground">
                  Architectural Types & Rear-Yard Conditions
                </h2>
              </div>
              
              <p className="text-foreground/90 leading-relaxed text-base md:text-lg">
                Decatur’s residential fabric is defined by distinctive historic and
                mid-century architecture. Rebuilding or expanding an outdoor deck requires
                evaluating how original framing, foundation heights, and terrain
                interact with new structural loads and modern building methods.
              </p>

              <div className="space-y-6 pt-2">
                <div className="border-l-2 border-chart-1/40 pl-5 space-y-2">
                  <h3 className="font-serif text-xl font-medium text-foreground">
                    Historic Craftsman Bungalows & Victorian Cottages
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Most 1910s–1930s bungalows in neighborhoods such as Oakhurst, Winnona Park,
                    and the MAK District rest on raised brick piers. The finished interior floor is
                    frequently 30 to 48 inches above ground grade at the rear. Connecting a new deck
                    to these homes demands particular care regarding the existing sill plate and rim
                    joist connections. In many early residences, masonry veneer or heart-pine
                    framing requires freestanding structural posts rather than ledger-board
                    attachment to prevent moisture infiltration and wood decay behind historic cladding.
                  </p>
                </div>

                <div className="border-l-2 border-chart-1/40 pl-5 space-y-2">
                  <h3 className="font-serif text-xl font-medium text-foreground">
                    Mid-Century Brick Ranches & Split-Levels
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Homes built in the 1950s and 1960s throughout Clairemont, Westchester, and
                    Medlock Park present different topography challenges. Rear lots frequently slope
                    sharply down into natural drainage corridors. Deck designs for these properties
                    often require multi-tier platforms, deep concrete footings to counter soil movement,
                    and integrated walkout basement access. Direct attachment to brick veneer without
                    through-bolting into primary structural floor joists is prohibited by code.
                  </p>
                </div>

                <div className="border-l-2 border-chart-1/40 pl-5 space-y-2">
                  <h3 className="font-serif text-xl font-medium text-foreground">
                    Microclimates, Moisture, & Mature Hardwood Canopies
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Decatur’s dense canopy of water oaks, white oaks, and tulip poplars creates
                    prolonged shade and organic debris accumulation. Decks in these microclimates
                    face chronic dampness and mildew vulnerability. Specifying joist-tape flashing,
                    minimum 1/4-inch surface board drainage spacing, and rot-resistant composite,
                    PVC, or sustainably sourced tropical hardwoods significantly reduces long-term
                    structural degradation.
                  </p>
                </div>
              </div>
            </article>

            <Separator className="bg-border/70" />

            {/* Section 2: Municipal Permitting */}
            <article className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-chart-2/10 text-chart-2">
                  <FileCheck className="size-5" />
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl font-normal text-foreground">
                  City of Decatur vs. Unincorporated DeKalb Permitting
                </h2>
              </div>

              <p className="text-foreground/90 leading-relaxed">
                Property location dictates regulatory jurisdiction. Homes within the 4.4-square-mile
                incorporated City of Decatur follow municipal codes managed by the City Hall Design,
                Environment &amp; Construction Division, while nearby addresses in unincorporated
                DeKalb County fall under county-level building administration.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <Card className="bg-card/70 border-border/80 shadow-sm rounded-xl">
                  <CardHeader className="pb-3">
                    <Badge variant="secondary" className="w-fit mb-1 font-mono text-xs">
                      Jurisdiction: City
                    </Badge>
                    <CardTitle className="font-serif text-lg text-foreground">
                      City of Decatur Standards
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-3">
                    <p>
                      <strong>Impervious Surface Maximums:</strong> Decatur enforces strict limits on
                      lot coverage (typically 40% maximum depending on specific zoning classification).
                      Decks with open slatted boards (minimum 1/8" gaps) over pervious earth are
                      often exempt, but covered porches or solid surfaces count directly against your cap.
                    </p>
                    <p>
                      <strong>Tree Canopy Protection Ordinance:</strong> Excavation for structural
                      piers within the Critical Root Zone (CRZ) of protected trees (over 8" DBH)
                      requires a designated Tree Protection Plan and city arborist approval.
                    </p>
                    <p>
                      <strong>Zoning Setbacks:</strong> Standard rear setbacks generally range between
                      20 and 30 feet, with side setbacks of 5 to 10 feet. Unenclosed decks may have
                      limited permissible encroachments.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-card/70 border-border/80 shadow-sm rounded-xl">
                  <CardHeader className="pb-3">
                    <Badge variant="secondary" className="w-fit mb-1 font-mono text-xs">
                      Jurisdiction: County
                    </Badge>
                    <CardTitle className="font-serif text-lg text-foreground">
                      Unincorporated DeKalb County
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-3">
                    <p>
                      <strong>Submittal &amp; Inspections:</strong> Plans are submitted to the DeKalb
                      County Department of Planning and Sustainability. Site plans must clearly indicate
                      floodplain proximity, stream buffers (75-foot county buffer on state waters), and
                      easements.
                    </p>
                    <p>
                      <strong>Footing Depth &amp; Ledger Code:</strong> DeKalb requires a minimum
                      12-inch frost line footing depth, footing hole inspections prior to concrete
                      pour, and strict adherence to the International Residential Code (IRC) Georgia
                      Amendments for deck tension ties and lateral load connectors.
                    </p>
                    <p>
                      <strong>Permit Exemptions:</strong> Free-standing decks under 200 square feet
                      that do not exceed 30 inches above adjacent grade and do not attach to the main
                      dwelling generally do not require a building permit in DeKalb.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </article>

            <Separator className="bg-border/70" />

            {/* Section 3: Historic District Design Review */}
            <article className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-chart-1/10 text-chart-1">
                  <Landmark className="size-5" />
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl font-normal text-foreground">
                  Historic District Design Review &amp; Visual Impact
                </h2>
              </div>

              <p className="text-foreground/90 leading-relaxed">
                If your property sits in a designated local historic district—including the
                McDonough-Adams-Kings (MAK) District, Old Decatur, Clairemont Avenue Historic District,
                or local conservation overlays—any exterior alteration or new structure requires a
                Certificate of Appropriateness (COA) from the Decatur Historic Preservation
                Commission (HPC) before building permits can be issued.
              </p>

              <div className="space-y-4 pt-1">
                <div className="p-5 rounded-xl bg-secondary/50 border border-border/60 space-y-3">
                  <h3 className="font-serif text-lg font-medium text-foreground flex items-center gap-2">
                    <Compass className="size-4 text-chart-1" />
                    Public Visibility &amp; Streetscape Character
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    The primary standard applied by preservation guidelines is whether the proposed deck
                    is visible from the public right-of-way (the primary street frontage or side street
                    on corner lots). Decks positioned strictly behind the rear elevation that remain
                    screened from public view typically receive administrative review, while elevated,
                    wraparound, or corner-visible structures require full Commission hearing review.
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-secondary/50 border border-border/60 space-y-3">
                  <h3 className="font-serif text-lg font-medium text-foreground flex items-center gap-2">
                    <Ruler className="size-4 text-chart-1" />
                    Material Compatibility &amp; Railing Profiles
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    HPC design guidelines prioritize traditional proportions. In historic zones,
                    handrails must emulate traditional millwork profiles rather than simple 2x4 top caps.
                    Square wood or historically proportioned balusters spaced under 4 inches apart
                    are favored. Modern cable-rail or horizontal bar systems may face review restrictions
                    if visible from adjacent historic streets, whereas traditional vertical balustrades
                    with painted or solid-stain finishes are generally supported.
                  </p>
                </div>
              </div>
            </article>

            {/* Action Callout Box */}
            <div className="p-6 md:p-8 rounded-xl bg-card border border-border shadow-md">
              <div className="max-w-2xl space-y-4">
                <h3 className="font-serif text-xl font-medium text-foreground">
                  Ready to Discuss Your Rear Yard or Deck Project?
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Learn more about our approach to exterior carpentry, site planning, and custom outdoor
                  renovations tailored to Decatur residences.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Button asChild size="default" className="font-medium">
                    <Link to="/OutdoorLivingDecksPage">View Outdoor Living Services</Link>
                  </Button>
                  <Button asChild variant="outline" size="default" className="font-medium">
                    <Link to="/ProcessPlanningPage">Explore Planning Process</Link>
                  </Button>
                </div>
              </div>
            </div>
          </main>

          {/* Sidebar / Quick Reference Panel */}
          <aside className="lg:col-span-4 space-y-6">
            <Card className="bg-card/90 border-border shadow-sm rounded-xl sticky top-24">
              <CardHeader className="pb-4">
                <CardTitle className="font-serif text-xl text-foreground flex items-center gap-2">
                  <Hammer className="size-5 text-chart-1" />
                  Key Takeaways
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="space-y-1.5 pb-3 border-b border-border/60">
                  <div className="font-medium text-foreground flex items-center gap-2">
                    <ShieldCheck className="size-4 text-chart-1" />
                    Ledger Board Attachments
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Never fasten ledgers directly to brick veneer or rotting sill plates. Freestanding
                    footing configurations preserve historic masonry structures.
                  </p>
                </div>

                <div className="space-y-1.5 pb-3 border-b border-border/60">
                  <div className="font-medium text-foreground flex items-center gap-2">
                    <Trees className="size-4 text-chart-1" />
                    Decatur Tree Canopy
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Pier hole digging inside the Critical Root Zone must be approved by the City
                    Arborist to prevent specimen tree loss.
                  </p>
                </div>

                <div className="space-y-1.5 pb-3 border-b border-border/60">
                  <div className="font-medium text-foreground flex items-center gap-2">
                    <Landmark className="size-4 text-chart-1" />
                    COA Requirements
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    In historic zones (MAK, Old Decatur), visible decks require Historic Preservation
                    Commission Certificate of Appropriateness prior to permitting.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <div className="font-medium text-foreground flex items-center gap-2">
                    <Compass className="size-4 text-chart-1" />
                    Lot Coverage Math
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Verify whether your proposed deck structure, stair landings, or porch roofs count
                    against city impervious surface limits before finalized plans.
                  </p>
                </div>

                <div className="pt-3">
                  <Button asChild variant="secondary" className="w-full text-xs font-medium">
                    <Link to="/ContactPage">Contact Project Team</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </section>;
}